import { throwIfAborted } from '../abort.js';
import { decimalToUnits } from '../amount/money.js';
import { fetchFundingQuote } from '../checkout/fetch-funding-quote.js';
import { fundingSavedCards } from '../discovery/saved-cards.js';
import { fundingSettlement } from '../discovery/settlement-asset.js';
import { refreshCustomerKyc } from './customer-kyc.js';
/** Read the current session's customer; contacts never select another Morfi owner. */
export async function readFundingProfile(client, provider, signal, connection, intent, includeLimits = true, currency) {
    const payer = await client.getPayerStatus(signal);
    throwIfAborted(signal);
    if (!connection.guest && !connection.verification && !connection.canConnect)
        throw new Error('onramp_profile_unavailable');
    // Checkout has already consumed the one-use session token. The established
    // connection binds this customer; profile refresh must not exchange it again.
    const verification = connection.verification
        ? await refreshCustomerKyc(provider, connection.verification.customerId)
        : null;
    throwIfAborted(signal);
    let savedCards = [];
    let savedCardsUnavailable = false;
    if (verification && currency) {
        try {
            const result = await provider.getPaymentMethods({ currency });
            throwIfAborted(signal);
            if (!result.ok)
                throw new Error('onramp_saved_cards_unavailable');
            const config = result.value.data.paymentMethodConfigs?.find((method) => method.type === 'card');
            savedCards = fundingSavedCards(result.value.data.paymentMethods ?? [], config?.capabilities.allowsDeletion === true);
        }
        catch {
            throwIfAborted(signal);
            savedCardsUnavailable = true;
        }
    }
    let limits = [];
    const authorized = connection.guest || Boolean(connection.verification);
    let limitsUnavailable = includeLimits && authorized && !intent;
    if (intent && authorized) {
        try {
            const context = await client.getContext(signal);
            throwIfAborted(signal);
            if (context.destination !== intent.destination ||
                context.asset !== intent.settlement.provider_asset_code)
                throw new Error('onramp_profile_quote_context_changed');
            limits = (await fetchFundingQuote(provider, intent, signal)).review.limits;
            limitsUnavailable = limits.length === 0;
        }
        catch {
            throwIfAborted(signal);
            limitsUnavailable = true;
        }
    }
    return {
        limits,
        limitsUnavailable,
        payer,
        verification,
        guest: connection.guest,
        canConnect: connection.canConnect ?? false,
        savedCards,
        savedCardsUnavailable,
    };
}
export function fundingProfilePresentation(data, limits) {
    const verification = data?.verification;
    return {
        requirements: verification
            ? Object.entries(verification.requirements).map(([category, requirement]) => ({ category, status: requirement.status }))
            : [],
        limits,
        canVerify: Boolean(data?.canConnect ||
            data?.guest ||
            (verification &&
                (verification.status === 'collecting' ||
                    verification.status === 'not_created' ||
                    verification.challenge))),
    };
}
/** A profile quote only reads allowances; it never creates an operation or changes checkout. */
export function fundingProfileQuoteIntent(connection, currency, decimals, previous) {
    if (!connection.guest && !connection.verification)
        return undefined;
    const current = connection.quoteContext;
    const method = current?.methods[0];
    if (!current || !method)
        return undefined;
    const { context } = current;
    if (!context.destination)
        return undefined;
    if (previous &&
        previous.destination === context.destination &&
        previous.settlement.provider_asset_code === context.asset &&
        current.methods.includes(previous.method) &&
        previous.fiatCurrency === currency &&
        previous.fiatDecimals === decimals)
        return previous;
    return {
        settlement: fundingSettlement(context.asset),
        destination: context.destination,
        method,
        fiatCurrency: currency,
        fiatDecimals: decimals,
        principalUnits: decimalToUnits('50', decimals).toString(),
    };
}
